import React, { useLayoutEffect, useRef } from "react";
import heroPhoto from "../../assets/blogphoto/stp52.jpg";
import {
  gsap,
  parallax,
  prefersReducedMotion,
  scrollToSection as scrollTo,
} from "../../lib/motion";
import "./hero.css";

/* Only facts already established by the organisation appear here — no
   invented figures. */
const FACTS = [
  { label: "Based in", value: "Barishal, Bangladesh" },
  { label: "Focus", value: "Stroke, paralysis & disability" },
  { label: "Working with", value: "SPRC Barishal" },
];

const Hero = () => {
  const root = useRef(null);
  const photo = useRef(null);

  useLayoutEffect(() => {
    const node = root.current;
    if (!node || prefersReducedMotion()) return undefined;

    const ctx = gsap.context(() => {
      /* The masthead sets itself the way a page is pasted up: rule, headline,
         standfirst, buttons, then the plate. */
      gsap
        .timeline({ defaults: { ease: "power3.out" } })
        .from(".hero__marker", { opacity: 0, y: 14, duration: 0.6 })
        .from(".hero__title", { opacity: 0, y: 26, duration: 0.9 }, "-=0.35")
        .from(".hero__lead", { opacity: 0, y: 18, duration: 0.7 }, "-=0.6")
        .from(
          ".hero__actions > *",
          { opacity: 0, y: 14, duration: 0.55, stagger: 0.08 },
          "-=0.45"
        )
        .from(
          ".hero__frame",
          { opacity: 0, y: 30, clipPath: "inset(0% 0% 100% 0%)", duration: 1 },
          "-=0.85"
        )
        .from(".hero__caption", { opacity: 0, duration: 0.5 }, "-=0.3")
        .from(
          ".hero__fact",
          { opacity: 0, y: 12, duration: 0.5, stagger: 0.07 },
          "-=0.55"
        );
    }, node);

    /* Oversize the plate first so the drift never exposes the frame edge. */
    gsap.set(photo.current, { scale: 1.07 });
    const stopParallax = parallax(photo.current, 44);

    return () => {
      stopParallax();
      ctx.revert();
    };
  }, []);

  return (
    <section
      className="hero on-dark"
      id="home"
      aria-labelledby="hero-title"
      ref={root}
    >
      <div className="hero__field" aria-hidden="true" />

      <div className="shell shell--wide hero__inner">
        <div className="hero__copy">
          <p className="marker marker--on-dark hero__marker">
            <span className="marker__num">SALES</span>
            <span>
              Social Advancement for Livelihood &amp; Educational Support
            </span>
          </p>

          <h1 id="hero-title" className="t-display-1 hero__title">
            Recovery is measured in degrees.{" "}
            <em className="hero__title-accent">We work for every one.</em>
          </h1>

          <p className="t-lead hero__lead">
            SALES supports people living with stroke, paralysis and disability
            in Barishal — through physiotherapy, help with the cost of
            treatment, inclusive schooling and livelihood training.
          </p>

          <div className="hero__actions">
            <button
              type="button"
              className="btn-sales btn-sales--donate"
              onClick={() => scrollTo("possibility")}
            >
              Donate
            </button>
            <button
              type="button"
              className="btn-sales btn-sales--ghost-dark"
              onClick={() => scrollTo("whatsprc")}
            >
              See what we do
            </button>
          </div>
        </div>

        <figure className="hero__figure">
          <div className="hero__frame">
            {/* Nothing is laid over this. A dial small enough to sit in the
                corner is too small to read as a dial, and it landed on the
                man's face — the photograph is the strongest thing here and
                it gets the whole frame. */}
            <img
              ref={photo}
              src={heroPhoto}
              alt="Clinicians attending to a man resting on a mat at a SALES rehabilitation camp, with families waiting behind them."
              width={1280}
              height={960}
              fetchpriority="high"
              decoding="async"
            />
          </div>
          <figcaption className="caption hero__caption">
            From one of our rehabilitation camps.
          </figcaption>
        </figure>
      </div>

      <div className="shell shell--wide">
        <dl className="hero__facts">
          {FACTS.map((fact) => (
            <div className="hero__fact" key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};

export default Hero;
